// components/views/profile/ProfilePage.jsx
import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HTTPService } from '../../../services/httpService';
import { toast } from 'react-toastify';
import moment from 'moment';
import PaymentSetup from './PaymentSetup';
import QuickBooksSetup from './QuickBooksSetup';
import QboSetupWizard from './QboSetupWizard';
import QboAPI from '../../../services/qboService';
import LocationService, { getDefaultOperatingHours, formatTime, formatHolidayDate, US_HOLIDAYS } from '../../../services/locationService';
import { isMultiLocation, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import Autocomplete from 'react-google-autocomplete';
import { QRCodeCanvas } from 'qrcode.react';

const API = new HTTPService();

// Time tracking mode options
const TIME_TRACKING_OPTIONS = [
  { value: 'none', label: 'Disabled', description: 'Time tracking features are hidden' },
  { value: 'voluntary', label: 'Voluntary', description: 'Staff can optionally check in/out' },
  { value: 'required', label: 'Required', description: 'Staff must check in before starting work' }
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

class ProfilePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      userId: this.props.userId,
      installerId: this.props.entityId,
      user: null,
      installer: null,
      loading: true,
      error: null,
      onboardingStep: 'overview', // overview, payment, complete
      paymentSetupStatus: 'pending', // pending, in_progress, complete

      // Company Settings Modal
      showSettingsModal: false,
      settingsLoading: false,
      settingsForm: {
        materials_tracking_enabled: true,
        time_tracking_mode: 'none'
      },

      // Payment
      paymentLoading: false,

      // Per-location data: { [locationId]: { operatingHours, isCalendarConnected, calendarLoading } }
      locationData: {},

      // Which location card is expanded (multi-loc only)
      expandedLocationId: null,

      // Operating hours modal — tracks which location
      operatingHoursModalLocationId: null,
      showOperatingHoursModal: false,
      operatingHoursForm: [],

      // Calendar selection modal — tracks which location
      calendarSelectionLocationId: null,
      showCalendarSelection: false,
      availableCalendars: [],

      // Holidays & closures — tracks which location
      blockedDatesLocationId: null,
      showHolidaysModal: false,
      holidayBlockedDates: [],
      holidaysLoading: false,

      // Custom closures modal
      showCustomClosuresModal: false,
      customClosures: [],
      newClosureStart: '',
      newClosureEnd: '',
      newClosureReason: '',
      closuresLoading: false,

      // Add Location modal
      showAddLocationModal: false,
      addLocationForm: { location_name: '', address_line1: '', address_line2: '', address_city: '', address_state: '', address_zip: '' },
      addLocationLoading: false,
      addLocationError: null,

      // QuickBooks
      qbo: null,
      qboLoading: false,
      showQboSetupWizard: false,

      // QR / Share link
      linkCopied: false
    };
  }

  async componentDidMount() {
    await this.loadUserProfile();
    this.checkOnboardingStatus();
    await this.loadAllLocationData();
    this.handleOAuthRedirect();
  }

  loadUserProfile = async () => {
    try {
      const userResponse = await API.get(`/profile/${this.state.userId}`);
      const installerResponse = await API.get(`/installers/${this.state.installerId}`);

      let stripeCapabilities = {};
      if (installerResponse.data[0]?.stripe_account_id && installerResponse.data[0]?.onboarding_complete) {
        try {
          const capResponse = await API.get(`/api/installers/${this.state.installerId}/stripe-status`);
          stripeCapabilities = capResponse.data || {};
        } catch (e) {
          console.error('Error loading Stripe capabilities:', e);
        }
      }

      // Fetch QBO connection status
      let qbo = null;
      try {
        qbo = await QboAPI.getConnection(this.state.installerId);
      } catch (e) {
        // QBO not set up yet — that's fine
      }

      this.setState({
        user: userResponse.data[0],
        installer: installerResponse.data[0],
        stripeCapabilities,
        qbo,
        loading: false
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      this.setState({
        error: 'Failed to load profile information',
        loading: false
      });
    }
  };

  checkOnboardingStatus = () => {
    const { installer } = this.state;
    if (!installer) return;

    if (installer.stripe_account_id && installer.onboarding_complete) {
      this.setState({ onboardingStep: 'complete', paymentSetupStatus: 'complete' });
    } else if (installer.stripe_account_id && !installer.onboarding_complete) {
      this.setState({ onboardingStep: 'payment', paymentSetupStatus: 'in_progress' });
    } else {
      this.setState({ onboardingStep: 'payment', paymentSetupStatus: 'pending' });
    }
  };

  handlePaymentSetupComplete = () => {
    this.setState({ onboardingStep: 'complete', paymentSetupStatus: 'complete' });
    this.loadUserProfile();
  };

  handleManagePayment = async () => {
    try {
      this.setState({ paymentLoading: true });
      const response = await API.post('/stripe/continue-onboarding', {
        installerId: this.state.installer.Installer_id
      });
      if (response.data.accountLinkUrl) {
        window.location.href = response.data.accountLinkUrl;
      }
    } catch (error) {
      console.error('Error opening payment management:', error);
      toast.error('Failed to open payment management. Please try again.');
      this.setState({ paymentLoading: false });
    }
  };

  // ── Company Settings ──────────────────────────────────────────────

  handleOpenSettingsModal = () => {
    const { installer } = this.state;
    this.setState({
      showSettingsModal: true,
      settingsForm: {
        materials_tracking_enabled: installer?.materials_tracking_enabled !== false,
        time_tracking_mode: installer?.time_tracking_mode || 'none'
      }
    });
  };

  handleCloseSettingsModal = () => {
    this.setState({ showSettingsModal: false });
  };

  handleSettingsChange = (field, value) => {
    this.setState(prevState => ({
      settingsForm: { ...prevState.settingsForm, [field]: value }
    }));
  };

  handleSaveSettings = async () => {
    const { installer, settingsForm } = this.state;
    try {
      this.setState({ settingsLoading: true });
      await API.put(`/api/installer-location/${installer.Installer_id}/time-settings`, {
        materials_tracking_enabled: settingsForm.materials_tracking_enabled,
        time_tracking_mode: settingsForm.time_tracking_mode,
        actor_user_id: this.props.userId
      });

      this.setState(prevState => ({
        installer: {
          ...prevState.installer,
          materials_tracking_enabled: settingsForm.materials_tracking_enabled,
          time_tracking_mode: settingsForm.time_tracking_mode
        },
        showSettingsModal: false,
        settingsLoading: false
      }));

      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
      companyData.materials_tracking_enabled = settingsForm.materials_tracking_enabled;
      companyData.time_tracking_mode = settingsForm.time_tracking_mode;
      localStorage.setItem('companyAPI', JSON.stringify(companyData));

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings. Please try again.');
      this.setState({ settingsLoading: false });
    }
  };

  getTimeTrackingLabel = (mode) => {
    const option = TIME_TRACKING_OPTIONS.find(o => o.value === mode);
    return option ? option.label : 'Disabled';
  };

  // ── Per-Location Data Loading ─────────────────────────────────────

  loadAllLocationData = async () => {
    const locations = this.props.locations || [];
    if (locations.length === 0) return;

    const results = await Promise.all(
      locations.map(loc => this.loadLocationData(loc.location_id))
    );

    const locationData = {};
    locations.forEach((loc, i) => {
      locationData[loc.location_id] = results[i];
    });
    this.setState({ locationData });
  };

  loadLocationData = async (locationId) => {
    const data = { operatingHours: getDefaultOperatingHours(), isCalendarConnected: false, calendarLoading: false };

    // Load operating hours
    try {
      const hoursData = await LocationService.getOperatingHours(locationId);
      data.operatingHours = hoursData.hours || getDefaultOperatingHours();
    } catch (error) {
      console.error(`Error loading operating hours for location ${locationId}:`, error);
    }

    // Check calendar connection
    try {
      const locationParam = `?locationId=${locationId}`;
      const response = await API.get(`/installerAuth/${this.props.entityId}${locationParam}`);
      if (response.data && response.data[0]) {
        data.isCalendarConnected = response.data[0].authenticated;
      }
    } catch (error) {
      console.error(`Error checking calendar for location ${locationId}:`, error);
    }

    return data;
  };

  updateLocationData = (locationId, updates) => {
    this.setState(prevState => ({
      locationData: {
        ...prevState.locationData,
        [locationId]: {
          ...(prevState.locationData[locationId] || {}),
          ...updates
        }
      }
    }));
  };

  // ── Calendar Methods (location-scoped) ────────────────────────────

  connectCalendar = async (locationId) => {
    try {
      this.updateLocationData(locationId, { calendarLoading: true });
      // Store locationId for OAuth redirect recovery
      sessionStorage.setItem('profile_calendar_locationId', String(locationId));
      const locationParam = `?locationId=${locationId}`;
      const { data: authUrl } = await API.get(`/googlecal/auth/${this.props.entityId}${locationParam}`);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Calendar connection error:', error);
      toast.error('Failed to connect calendar. Please try again.');
      this.updateLocationData(locationId, { calendarLoading: false });
    }
  };

  handleOAuthRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search);

    // QuickBooks OAuth callback
    if (urlParams.get('qbo_connected')) {
      window.history.replaceState({}, document.title, window.location.pathname);
      toast.success('QuickBooks connected successfully!');
      this.loadUserProfile();
      return;
    }
    if (urlParams.get('qbo_error')) {
      const msg = urlParams.get('message') || urlParams.get('qbo_error');
      window.history.replaceState({}, document.title, window.location.pathname);
      toast.error(`QuickBooks connection failed: ${msg === 'true' ? 'Unknown error' : msg}`);
      return;
    }

    if (urlParams.get('calendar_selection')) {
      try {
        const calendarsParam = urlParams.get('calendars');
        const installerId = urlParams.get('installer_id');
        const calendars = JSON.parse(calendarsParam);

        if (installerId === this.props.entityId.toString()) {
          // Recover locationId from sessionStorage
          const locationId = sessionStorage.getItem('profile_calendar_locationId');
          sessionStorage.removeItem('profile_calendar_locationId');

          this.setState({
            showCalendarSelection: true,
            availableCalendars: calendars,
            calendarSelectionLocationId: locationId ? Number(locationId) || locationId : null
          });
        }

        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.error('Error parsing calendar list:', e);
        toast.error('Error loading calendar options. Please try again.');
      }
    }
  };

  handleCalendarSelection = async (selectedCalendar) => {
    const { calendarSelectionLocationId } = this.state;
    try {
      const body = {
        installerId: this.props.entityId,
        calendarId: selectedCalendar.id
      };
      if (calendarSelectionLocationId) {
        body.locationId = calendarSelectionLocationId;
      }
      await API.post('/googlecal/select', body);

      this.setState({ showCalendarSelection: false, availableCalendars: [], calendarSelectionLocationId: null });

      if (calendarSelectionLocationId) {
        this.updateLocationData(calendarSelectionLocationId, { isCalendarConnected: true, calendarLoading: false });
      }

      window.dispatchEvent(new CustomEvent('calendarStatusChanged', { detail: { connected: true } }));
      toast.success(`Calendar "${selectedCalendar.name}" connected successfully!`);
    } catch (error) {
      console.error('Error saving calendar selection:', error);
      toast.error('Failed to save calendar selection. Please try again.');
    }
  };

  handleCloseCalendarSelection = () => {
    this.setState({ showCalendarSelection: false, availableCalendars: [], calendarSelectionLocationId: null });
  };

  disconnectCalendar = async (locationId) => {
    if (!window.confirm('Are you sure you want to disconnect Google Calendar for this location?')) return;

    try {
      this.updateLocationData(locationId, { calendarLoading: true });
      const locationParam = `?locationId=${locationId}`;
      await API.delete(`/googlecal/disconnect/${this.props.entityId}${locationParam}`);

      this.updateLocationData(locationId, { isCalendarConnected: false, calendarLoading: false });
      window.dispatchEvent(new CustomEvent('calendarStatusChanged', { detail: { connected: false } }));
      toast.success('Calendar disconnected successfully.');
    } catch (error) {
      console.error('Calendar disconnection error:', error);
      toast.error('Failed to disconnect calendar. Please try again.');
      this.updateLocationData(locationId, { calendarLoading: false });
    }
  };

  // ── Operating Hours Methods (location-scoped) ─────────────────────

  handleOpenOperatingHoursModal = (locationId) => {
    const locData = this.state.locationData[locationId] || {};
    const formHours = locData.operatingHours && locData.operatingHours.length > 0
      ? JSON.parse(JSON.stringify(locData.operatingHours))
      : getDefaultOperatingHours();
    this.setState({
      showOperatingHoursModal: true,
      operatingHoursModalLocationId: locationId,
      operatingHoursForm: formHours
    });
  };

  handleCloseOperatingHoursModal = () => {
    this.setState({ showOperatingHoursModal: false, operatingHoursModalLocationId: null });
  };

  handleOperatingHoursChange = (dayIndex, field, value) => {
    this.setState(prevState => {
      const newForm = [...prevState.operatingHoursForm];
      newForm[dayIndex] = { ...newForm[dayIndex], [field]: value };
      if (field === 'is_open' && !value) {
        newForm[dayIndex].open_time = null;
        newForm[dayIndex].close_time = null;
      }
      if (field === 'is_open' && value && !newForm[dayIndex].open_time) {
        newForm[dayIndex].open_time = '08:00';
        newForm[dayIndex].close_time = '17:00';
      }
      return { operatingHoursForm: newForm };
    });
  };

  handleSaveOperatingHours = async () => {
    const { operatingHoursModalLocationId, operatingHoursForm } = this.state;
    try {
      this.setState({ operatingHoursLoading: true });
      await LocationService.saveOperatingHours(operatingHoursModalLocationId, operatingHoursForm);

      this.updateLocationData(operatingHoursModalLocationId, { operatingHours: operatingHoursForm });
      this.setState({ showOperatingHoursModal: false, operatingHoursModalLocationId: null, operatingHoursLoading: false });
      toast.success('Operating hours saved successfully');
    } catch (error) {
      console.error('Error saving operating hours:', error);
      toast.error('Failed to save operating hours. Please try again.');
      this.setState({ operatingHoursLoading: false });
    }
  };

  // ── Holidays & Custom Closures (location-scoped) ───────────────────

  loadBlockedDates = async (locationId) => {
    try {
      const data = await LocationService.getBlockedDates(locationId);
      const blockedDates = data.blockedDates || [];

      const currentYear = moment().year();
      const nextYear = currentYear + 1;
      const holidayDatesSet = new Set();
      US_HOLIDAYS.forEach(holiday => {
        holidayDatesSet.add(holiday.getDate(currentYear));
        holidayDatesSet.add(holiday.getDate(nextYear));
      });

      const holidayBlockedDates = blockedDates.filter(bd => holidayDatesSet.has(bd.date));
      const nonHolidayDates = blockedDates.filter(bd => !holidayDatesSet.has(bd.date));
      const customClosures = this.groupIntoClosure(nonHolidayDates);

      this.setState({ holidayBlockedDates, customClosures });
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    }
  };

  groupIntoClosure = (blockedDates) => {
    if (!blockedDates.length) return [];
    const sorted = [...blockedDates].sort((a, b) => a.date.localeCompare(b.date));
    const closures = [];
    let current = null;

    for (const bd of sorted) {
      if (!current) {
        current = { start_date: bd.date, end_date: bd.date, reason: bd.reason };
      } else {
        const daysDiff = moment(bd.date).diff(moment(current.end_date), 'days');
        if (daysDiff === 1 && bd.reason === current.reason) {
          current.end_date = bd.date;
        } else {
          closures.push(current);
          current = { start_date: bd.date, end_date: bd.date, reason: bd.reason };
        }
      }
    }
    if (current) closures.push(current);
    return closures;
  };

  formatDateRange = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    if (start.isSame(end, 'day')) return start.format('ddd, MMM D, YYYY');
    if (start.isSame(end, 'year')) return `${start.format('ddd, MMM D')} - ${end.format('ddd, MMM D, YYYY')}`;
    return `${start.format('ddd, MMM D, YYYY')} - ${end.format('ddd, MMM D, YYYY')}`;
  };

  isDateBlocked = (date) => {
    return this.state.holidayBlockedDates.some(bd => bd.date === date);
  };

  // Holidays modal
  handleOpenHolidaysModal = async (locationId) => {
    await this.loadBlockedDates(locationId);
    this.setState({ showHolidaysModal: true, blockedDatesLocationId: locationId });
  };

  handleCloseHolidaysModal = () => {
    this.setState({ showHolidaysModal: false });
  };

  handleToggleHoliday = async (date, holidayName) => {
    const locationId = this.state.blockedDatesLocationId;
    const isCurrentlyBlocked = this.isDateBlocked(date);
    try {
      this.setState({ holidaysLoading: true });
      await LocationService.toggleHoliday(locationId, date, holidayName, isCurrentlyBlocked);
      await this.loadBlockedDates(locationId);
      this.setState({ holidaysLoading: false });
    } catch (error) {
      console.error('Error toggling holiday:', error);
      toast.error('Failed to update holiday. Please try again.');
      this.setState({ holidaysLoading: false });
    }
  };

  // Custom closures modal
  handleOpenCustomClosuresModal = async (locationId) => {
    await this.loadBlockedDates(locationId);
    this.setState({
      showCustomClosuresModal: true,
      blockedDatesLocationId: locationId,
      newClosureStart: '',
      newClosureEnd: '',
      newClosureReason: ''
    });
  };

  handleCloseCustomClosuresModal = () => {
    this.setState({ showCustomClosuresModal: false });
  };

  handleAddCustomClosure = async () => {
    const { newClosureStart, newClosureEnd, newClosureReason, blockedDatesLocationId } = this.state;

    if (!newClosureStart || !newClosureEnd || !newClosureReason.trim()) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (moment(newClosureEnd).isBefore(moment(newClosureStart))) {
      toast.error('End date must be on or after start date.');
      return;
    }

    try {
      this.setState({ closuresLoading: true });
      await LocationService.addDateRange(blockedDatesLocationId, newClosureStart, newClosureEnd, newClosureReason.trim());
      await this.loadBlockedDates(blockedDatesLocationId);
      this.setState({ closuresLoading: false, newClosureStart: '', newClosureEnd: '', newClosureReason: '' });
      toast.success('Closure added successfully');
    } catch (error) {
      console.error('Error adding custom closure:', error);
      toast.error('Failed to add closure. Please try again.');
      this.setState({ closuresLoading: false });
    }
  };

  handleRemoveClosure = async (closure) => {
    if (!window.confirm(`Remove closure "${closure.reason}" from ${this.formatDateRange(closure.start_date, closure.end_date)}?`)) return;

    try {
      this.setState({ closuresLoading: true });
      await LocationService.removeDateRange(this.state.blockedDatesLocationId, closure.start_date, closure.end_date);
      await this.loadBlockedDates(this.state.blockedDatesLocationId);
      this.setState({ closuresLoading: false });
      toast.success('Closure removed');
    } catch (error) {
      console.error('Error removing closure:', error);
      toast.error('Failed to remove closure. Please try again.');
      this.setState({ closuresLoading: false });
    }
  };

  // ── Add Location Methods ─────────────────────────────────────────

  handleOpenAddLocationModal = () => {
    this.setState({
      showAddLocationModal: true,
      addLocationForm: { location_name: '', address_line1: '', address_line2: '', address_city: '', address_state: '', address_zip: '' },
      addLocationError: null
    });
  };

  handleCloseAddLocationModal = () => {
    this.setState({ showAddLocationModal: false });
  };

  handleAddLocationChange = (field, value) => {
    this.setState(prevState => ({
      addLocationForm: { ...prevState.addLocationForm, [field]: value }
    }));
  };

  handleAddLocationPlaceSelected = (place) => {
    if (!place.address_components) return;

    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zipCode = '';

    place.address_components.forEach(component => {
      const types = component.types;
      if (types.includes('street_number')) streetNumber = component.long_name;
      if (types.includes('route')) route = component.long_name;
      if (types.includes('locality')) city = component.long_name;
      if (types.includes('administrative_area_level_1')) state = component.short_name;
      if (types.includes('postal_code')) zipCode = component.long_name;
    });

    this.setState(prevState => ({
      addLocationForm: {
        ...prevState.addLocationForm,
        address_line1: [streetNumber, route].filter(Boolean).join(' '),
        address_city: city,
        address_state: state,
        address_zip: zipCode
      }
    }));
  };

  handleAddLocationSubmit = async () => {
    const { addLocationForm } = this.state;
    const { location_name, address_line1, address_city, address_state } = addLocationForm;

    if (!location_name.trim() || !address_line1.trim() || !address_city.trim() || !address_state.trim()) {
      this.setState({ addLocationError: 'Location name, street address, city, and state are required.' });
      return;
    }

    try {
      this.setState({ addLocationLoading: true, addLocationError: null });
      await API.post(`/installers/${this.props.entityId}/locations`, addLocationForm);

      // Refresh locations in localStorage
      const locResponse = await API.get(`/installers/${this.props.entityId}/locations`);
      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
      companyData.locations = locResponse.data;
      localStorage.setItem('companyAPI', JSON.stringify(companyData));

      // If transitioning single→multi, set to all-locations view
      if ((this.props.locations || []).length === 1) {
        localStorage.setItem('selectedLocationId', 'all');
      }

      toast.success('Location added successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Error adding location:', error);
      this.setState({
        addLocationError: error.response?.data?.message || 'Failed to add location. Please try again.',
        addLocationLoading: false
      });
    }
  };

  // ── Location Expand/Collapse ──────────────────────────────────────

  toggleLocationExpand = (locationId) => {
    this.setState(prevState => ({
      expandedLocationId: prevState.expandedLocationId === locationId ? null : locationId
    }));
  };

  // ── Location Render Helpers ───────────────────────────────────────

  renderLocationAddress = (location) => {
    if (location.address_line1) {
      return (
        <>
          {location.address_line1}
          {location.address_line2 && <><br />{location.address_line2}</>}
          <br />
          {[location.address_city, location.address_state].filter(Boolean).join(', ')} {location.address_zip || ''}
        </>
      );
    }
    // Fallback: some locations may use installer-prefixed fields
    if (location.installer_address_line1) {
      return (
        <>
          {location.installer_address_line1}
          {location.installer_address_line2 && <><br />{location.installer_address_line2}</>}
          <br />
          {[location.installer_address_city, location.installer_state || location.installer_address_state].filter(Boolean).join(', ')} {location.installer_address_zip || ''}
        </>
      );
    }
    return <span className="text-muted">No address set</span>;
  };

  getHoursSummary = (hours) => {
    if (!hours || hours.length === 0) return 'Not set';
    const openDays = hours.filter(d => d.is_open);
    if (openDays.length === 0) return 'Closed all days';
    // Find the most common hours pattern
    const firstOpen = openDays[0];
    return `${openDays.length} days open, ${formatTime(firstOpen.open_time)}-${formatTime(firstOpen.close_time)}`;
  };

  renderLocationSummary = (location, locData) => {
    const locations = this.props.locations || [];
    const isExpanded = this.state.expandedLocationId === location.location_id;
    const color = getLocationBadgeColor(location.location_id, locations);

    return (
      <div
        key={location.location_id}
        className="card mb-3"
        style={{ borderLeft: `4px solid ${color}` }}
      >
        <div
          className="card-header d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => this.toggleLocationExpand(location.location_id)}
        >
          <div>
            <h5 className="mb-0 d-inline-block mr-3">{getLocationName(location)}</h5>
            <span className="text-muted small">
              {[location.address_city || location.installer_address_city, location.address_state || location.installer_state].filter(Boolean).join(', ')}
            </span>
          </div>
          <div className="d-flex align-items-center">
            {location.google_rating && (
              <span className="mr-3 small">
                <span style={{ color: '#f4b400' }}>&#9733;</span>{' '}
                {location.google_rating}
                {location.google_review_count && (
                  <span className="text-muted"> ({location.google_review_count})</span>
                )}
              </span>
            )}
            {!!locData?.isCalendarConnected && (
              <span className="badge badge-success mr-3">
                <i className="fa-sharp fa-regular fa-calendar-check mr-1"></i> Calendar
              </span>
            )}
            <i className={`fa-sharp fa-regular fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
          </div>
        </div>
        {isExpanded && (
          <div className="card-body">
            {this.renderLocationDetail(location, locData, { readOnly: true })}
          </div>
        )}
      </div>
    );
  };

  renderLocationDetail = (location, locData, { readOnly = false, showHolidayButtons = false } = {}) => {
    const hours = locData?.operatingHours || [];
    const isCalendarConnected = locData?.isCalendarConnected || false;
    const calendarLoading = locData?.calendarLoading || false;
    const locationId = location.location_id;

    return (
      <>
        {/* Address */}
        <div className="mb-4">
          <h6 className="font-weight-bold">
            <i className="fa-sharp fa-regular fa-map-marker-alt mr-2"></i>Address
          </h6>
          <div className="ml-4">
            {this.renderLocationAddress(location)}
          </div>
        </div>

        {/* Google Calendar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="font-weight-bold mb-0">
              <i className="fa-sharp fa-regular fa-calendar mr-2"></i>Google Calendar
            </h6>
            {!readOnly && (
              isCalendarConnected ? (
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => this.disconnectCalendar(locationId)}
                  disabled={calendarLoading}
                >
                  {calendarLoading ? 'Disconnecting...' : 'Disconnect'}
                </button>
              ) : (
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => this.connectCalendar(locationId)}
                  disabled={calendarLoading}
                >
                  {calendarLoading ? 'Connecting...' : 'Connect'}
                </button>
              )
            )}
          </div>
          <div className="ml-4">
            {isCalendarConnected ? (
              <>
                <span className="badge badge-success mr-2">
                  <i className="fa-sharp fa-regular fa-check mr-1"></i> Connected
                </span>
                <span className="text-muted">Jobs sync to your Google Calendar</span>
              </>
            ) : (
              <>
                <span className="badge badge-secondary mr-2">Not Connected</span>
                <span className="text-muted">Connect to sync jobs to Google Calendar</span>
              </>
            )}
          </div>
        </div>

        {/* Google Reviews */}
        {location.google_rating && (
          <div className="mb-4">
            <h6 className="font-weight-bold">
              <i className="fa-sharp fa-regular fa-star mr-2"></i>Google Reviews
            </h6>
            <div className="ml-4">
              <span style={{ color: '#f4b400', fontSize: '1.1rem', letterSpacing: '1px' }}>
                {'\u2605'.repeat(Math.floor(location.google_rating))}
                {location.google_rating - Math.floor(location.google_rating) >= 0.3 ? '\u00BD' : ''}
              </span>
              <span className="ml-2 font-weight-bold">{location.google_rating}</span>
              {location.google_review_count && (
                <span className="text-muted ml-1">({location.google_review_count} reviews)</span>
              )}
            </div>
          </div>
        )}

        {/* Operating Hours */}
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="font-weight-bold mb-0">
              <i className="fa-sharp fa-regular fa-clock mr-2"></i>Operating Hours
            </h6>
            {!readOnly && (
              <div>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => this.handleOpenOperatingHoursModal(locationId)}
                >
                  <i className="fa-sharp fa-regular fa-edit mr-1"></i> Edit
                </button>
                {showHolidayButtons && (
                  <>
                    <button
                      className="btn btn-outline-secondary btn-sm ml-2"
                      onClick={() => this.handleOpenHolidaysModal(locationId)}
                    >
                      <i className="fa-sharp fa-regular fa-calendar-star mr-1"></i> Holidays
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm ml-2"
                      onClick={() => this.handleOpenCustomClosuresModal(locationId)}
                    >
                      <i className="fa-sharp fa-regular fa-calendar-xmark mr-1"></i> Closures
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="ml-4">
            {hours.length > 0 ? (
              <div className="operating-hours-grid">
                {hours.map((day, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center py-1">
                    <span className="font-weight-bold" style={{ width: '100px' }}>
                      {day.day_name || DAY_NAMES[day.day_of_week]}
                    </span>
                    <span className={day.is_open ? 'text-success' : 'text-muted'}>
                      {day.is_open
                        ? `${formatTime(day.open_time)} - ${formatTime(day.close_time)}`
                        : 'Closed'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-muted">No hours configured</span>
            )}
          </div>
        </div>
      </>
    );
  };

  // ── Reusable Card Helpers ─────────────────────────────────────────

  renderCompanyInfoCard = () => {
    const { installer } = this.state;
    return (
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">Company Information</h4>
        </div>
        <div className="card-body">
          {installer && (
            <div className="row">
              <div className="col-md-4 mb-3">
                <strong>Business Name:</strong><br />
                {installer.Installer_Name || 'Not set'}
              </div>
              <div className="col-md-4 mb-3">
                <strong>Email:</strong><br />
                {installer.installer_email || installer.Email || 'Not set'}
              </div>
              <div className="col-md-4 mb-3">
                <strong>Phone:</strong><br />
                {installer.installer_phone_number || 'Not set'}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  renderCompanySettingsCard = (editable = false) => {
    const { installer } = this.state;
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Company Settings</h4>
          {editable && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={this.handleOpenSettingsModal}
            >
              <i className="fa-sharp fa-regular fa-edit mr-1"></i> Edit
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Materials Tracking:</strong><br />
              <span className={`badge ${installer?.materials_tracking_enabled !== false ? 'badge-success' : 'badge-secondary'}`}>
                {installer?.materials_tracking_enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
              <p className="text-muted small mt-1 mb-0">
                {installer?.materials_tracking_enabled !== false
                  ? 'Technicians will be prompted to record materials used when finishing services'
                  : 'Materials collection is disabled'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Time Tracking:</strong><br />
              <span className={`badge ${(installer?.time_tracking_mode && installer.time_tracking_mode !== 'none') ? 'badge-success' : 'badge-secondary'}`}>
                {this.getTimeTrackingLabel(installer?.time_tracking_mode)}
              </span>
              <p className="text-muted small mt-1 mb-0">
                {installer?.time_tracking_mode === 'required'
                  ? 'Staff must check in before starting work on jobs'
                  : installer?.time_tracking_mode === 'voluntary'
                  ? 'Staff can optionally check in/out to track hours'
                  : 'Time tracking features are hidden'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderPaymentCard = () => {
    const { installer } = this.state;
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Payment Account</h4>
          {installer?.stripe_account_id && installer?.onboarding_complete && (
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={this.handleManagePayment}
              disabled={this.state.paymentLoading}
            >
              {this.state.paymentLoading ? 'Loading...' : 'Manage Account'}
            </button>
          )}
        </div>
        <div className="card-body">
          <PaymentSetup
            installer={installer}
            stripeCapabilities={this.state.stripeCapabilities}
            onSetupComplete={this.handlePaymentSetupComplete}
            hideManageButton={true}
          />
        </div>
      </div>
    );
  };

  renderQuickBooksCard = () => {
    const { installer, qbo } = this.state;
    const isConnected = qbo && qbo.connected && !qbo.token_expired;

    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">QuickBooks Account</h4>
          {(isConnected || (qbo && qbo.token_expired)) && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={this.handleQboDisconnect}
              disabled={this.state.qboLoading}
            >
              {this.state.qboLoading ? 'Disconnecting...' : 'Disconnect'}
            </button>
          )}
        </div>
        <div className="card-body">
          <QuickBooksSetup
            installer={installer}
            qbo={qbo}
            onStatusChange={this.handleQboStatusChange}
            onSetupClick={this.handleOpenQboSetupWizard}
          />
        </div>
      </div>
    );
  };

  handleQboStatusChange = () => {
    this.loadUserProfile();
  };

  handleQboDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect QuickBooks? Sync history will be preserved.')) return;
    this.setState({ qboLoading: true });
    try {
      await QboAPI.disconnect(this.state.installer.Installer_id);
      await this.loadUserProfile();
    } catch (error) {
      console.error('QBO disconnect error:', error);
      toast.error('Failed to disconnect QuickBooks');
    }
    this.setState({ qboLoading: false });
  };

  // ── QBO Setup Wizard ─────────────────────────────────────────────

  handleOpenQboSetupWizard = () => {
    this.setState({ showQboSetupWizard: true });
  };

  handleCloseQboSetupWizard = () => {
    this.setState({ showQboSetupWizard: false });
  };

  handleQboActivated = () => {
    this.setState({ showQboSetupWizard: false });
    toast.success('QuickBooks sync activated successfully!');
    this.loadUserProfile();
  };

  // ── QR / Share Link Helpers ──────────────────────────────────────

  getEstimateUrl = () => {
    const { installer } = this.state;
    const base = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    if (installer?.slug) {
      return `${base}/estimate/${installer.slug}`;
    }
    return `${base}/estimate?installer=${this.props.entityId}`;
  };

  handleCopyLink = () => {
    const url = this.getEstimateUrl();
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copied to clipboard!');
      this.setState({ linkCopied: true });
      setTimeout(() => this.setState({ linkCopied: false }), 3000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  handleDownloadQRCode = () => {
    const canvas = document.getElementById('estimate-qr-code');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    const { installer } = this.state;
    link.download = `${installer?.slug || 'estimate'}-qr-code.png`;
    link.href = url;
    link.click();
  };

  // ── Main Render ───────────────────────────────────────────────────

  render() {
    const {
      installer, loading, error, onboardingStep, paymentSetupStatus,
      showSettingsModal, settingsForm, settingsLoading,
      showCalendarSelection, availableCalendars,
      showOperatingHoursModal, operatingHoursForm, operatingHoursLoading,
      locationData
    } = this.state;

    if (loading) {
      return (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3">Loading profile...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="alert alert-danger">
                <h4>Error Loading Profile</h4>
                <p>{error}</p>
                <button className="btn btn-outline-danger" onClick={this.loadUserProfile}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const locations = this.props.locations || [];
    const isInstaller = this.props.userType === 'installer';
    const multiLoc = isMultiLocation(locations);
    const selectedLocId = this.props.selectedLocationId;
    const isSingleLocCombined = isInstaller && locations.length === 1;
    const isLocationLevel = isInstaller && !isSingleLocCombined && selectedLocId && selectedLocId !== 'all';
    const selectedLocation = isLocationLevel
      ? locations.find(l => String(l.location_id) === String(selectedLocId))
      : null;
    const selectedLocData = selectedLocation ? locationData[selectedLocation.location_id] : null;
    const isOwnerOrAdmin = ['owner', 'admin'].includes(this.props.userRole);

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-header">
              <h1>Profile Settings</h1>
              <p className="text-muted">Manage your account and business information</p>
            </div>
          </div>
        </div>

        {/* Share Your Estimate Page (installer only) */}
        {isInstaller && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fa-sharp fa-regular fa-share-nodes mr-2"></i>
                    Share Your Estimate Page
                  </h5>
                  <a
                    href={this.getEstimateUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-success"
                  >
                    <i className="fa-sharp fa-regular fa-external-link-alt mr-1"></i>
                    Preview
                  </a>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    {/* QR Code */}
                    <div className="col-md-4 text-center mb-3 mb-md-0">
                      <QRCodeCanvas
                        id="estimate-qr-code"
                        value={this.getEstimateUrl()}
                        size={180}
                        level="M"
                        includeMargin={true}
                      />
                      <div className="mt-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={this.handleDownloadQRCode}
                        >
                          <i className="fa-sharp fa-regular fa-download mr-1"></i>
                          Download QR Code
                        </button>
                      </div>
                    </div>

                    {/* Shareable Link */}
                    <div className="col-md-8">
                      <label className="font-weight-bold mb-1">Your Estimate Page Link</label>
                      <div className="input-group mb-2">
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={this.getEstimateUrl().replace(/^https?:\/\//, '')}
                          readOnly
                          onClick={(e) => e.target.select()}
                        />
                        <div className="input-group-append">
                          <button
                            className={`btn ${this.state.linkCopied ? 'btn-success' : 'btn-primary'}`}
                            onClick={this.handleCopyLink}
                          >
                            <i className={`fa-sharp fa-regular ${this.state.linkCopied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                            {this.state.linkCopied ? 'Copied!' : 'Copy Link'}
                          </button>
                        </div>
                      </div>
                      <small className="text-muted">
                        Share this link on business cards, shop signage, social media, or anywhere customers can find you.
                        The QR code links to the same page.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Main Content ────────────────────────────────────────── */}
        <div className="row">
          {/* Left Column */}
          <div className={isLocationLevel ? 'col-12' : 'col-md-8'}>

            {/* Trial Specialist Onboarding */}
            {!!installer.is_trial && onboardingStep !== 'complete' && (
              <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Complete Your Account Setup</h4>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="font-weight-bold">Setup Progress</span>
                      <span className="text-muted">
                        {onboardingStep === 'complete' ? '100%' : '50%'}
                      </span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar bg-success"
                        style={{ width: onboardingStep === 'complete' ? '100%' : '50%' }}
                      ></div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div className="mr-3">
                          <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '30px', height: '30px'}}>
                            ✓
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1">Account Created</h6>
                          <p className="text-muted mb-0">Your trial account is active</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <div className="mr-3">
                          <div className={`rounded-circle d-flex align-items-center justify-content-center ${
                            paymentSetupStatus === 'complete' ? 'bg-success text-white' : 'bg-warning text-dark'
                          }`} style={{width: '30px', height: '30px'}}>
                            {paymentSetupStatus === 'complete' ? '✓' : '2'}
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1">Payment Setup</h6>
                          <p className="text-muted mb-0">
                            {paymentSetupStatus === 'complete' ? 'Connected' : 'Required to receive payments'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {paymentSetupStatus !== 'complete' && (
                    <div className="alert alert-warning">
                      <h6 className="alert-heading">Payment Setup Required</h6>
                      <p className="mb-2">You must connect your bank account before completing any jobs.</p>
                      <p className="mb-0">This enables you to receive your job payments instantly.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isSingleLocCombined ? (
              <>
                {/* ── Single-Location Combined View ────────────────── */}

                {this.renderCompanyInfoCard()}
                {this.renderCompanySettingsCard(true)}

                {/* Location */}
                <div className="card mb-4">
                  <div className="card-header">
                    <h4 className="mb-0">Location</h4>
                  </div>
                  <div className="card-body">
                    {this.renderLocationDetail(locations[0], locationData[locations[0].location_id], { readOnly: false, showHolidayButtons: true })}
                  </div>
                </div>

                {this.renderPaymentCard()}
                {this.props.access === 'ADMIN' && this.renderQuickBooksCard()}

                {/* Add Location button (owner/admin only) */}
                {isOwnerOrAdmin && (
                  <div className="text-center mb-4">
                    <p className="text-muted mb-2">Expand your business by adding a second location</p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={this.handleOpenAddLocationModal}
                    >
                      <i className="fa-sharp fa-regular fa-plus mr-2"></i>Add Location
                    </button>
                  </div>
                )}
              </>
            ) : isLocationLevel && selectedLocation ? (
              <>
                {/* ── Location-Level View ──────────────────────────── */}

                {/* Location Information */}
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Location Information</h4>
                    {selectedLocData?.isCalendarConnected ? (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => this.disconnectCalendar(selectedLocation.location_id)}
                        disabled={selectedLocData?.calendarLoading}
                      >
                        {selectedLocData?.calendarLoading ? 'Disconnecting...' : 'Disconnect Calendar'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => this.connectCalendar(selectedLocation.location_id)}
                        disabled={selectedLocData?.calendarLoading}
                      >
                        {selectedLocData?.calendarLoading ? 'Connecting...' : 'Connect Calendar'}
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <strong>Address:</strong><br />
                        {this.renderLocationAddress(selectedLocation)}
                      </div>
                      <div className="col-md-6 mb-3">
                        <strong>Google Calendar:</strong><br />
                        {selectedLocData?.isCalendarConnected ? (
                          <>
                            <span className="badge badge-success mr-2">
                              <i className="fa-sharp fa-regular fa-check mr-1"></i> Connected
                            </span>
                            <span className="text-muted">Jobs sync to your Google Calendar</span>
                          </>
                        ) : (
                          <>
                            <span className="badge badge-secondary mr-2">Not Connected</span>
                            <span className="text-muted">Connect to sync jobs to Google Calendar</span>
                          </>
                        )}
                      </div>
                      {selectedLocation.google_rating && (
                        <div className="col-md-6 mb-3">
                          <strong>Google Reviews:</strong><br />
                          <span style={{ color: '#f4b400', fontSize: '1.1rem', letterSpacing: '1px' }}>
                            {'\u2605'.repeat(Math.floor(selectedLocation.google_rating))}
                            {selectedLocation.google_rating - Math.floor(selectedLocation.google_rating) >= 0.3 ? '\u00BD' : ''}
                          </span>
                          <span className="ml-2 font-weight-bold">{selectedLocation.google_rating}</span>
                          {selectedLocation.google_review_count && (
                            <span className="text-muted ml-1">({selectedLocation.google_review_count} reviews)</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Operating Hours */}
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Operating Hours</h4>
                    <div>
                      <button
                        className="btn btn-outline-primary btn-sm mr-2"
                        onClick={() => this.handleOpenOperatingHoursModal(selectedLocation.location_id)}
                      >
                        <i className="fa-sharp fa-regular fa-edit mr-1"></i> Edit
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm mr-2"
                        onClick={() => this.handleOpenHolidaysModal(selectedLocation.location_id)}
                      >
                        <i className="fa-sharp fa-regular fa-calendar-star mr-1"></i> Holidays
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => this.handleOpenCustomClosuresModal(selectedLocation.location_id)}
                      >
                        <i className="fa-sharp fa-regular fa-calendar-xmark mr-1"></i> Closures
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {(selectedLocData?.operatingHours || []).length > 0 ? (
                      <div className="operating-hours-grid">
                        {(selectedLocData?.operatingHours || []).map((day, index) => (
                          <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="font-weight-bold" style={{ width: '100px' }}>
                              {day.day_name || DAY_NAMES[day.day_of_week]}
                            </span>
                            <span className={day.is_open ? 'text-success' : 'text-muted'}>
                              {day.is_open
                                ? `${formatTime(day.open_time)} - ${formatTime(day.close_time)}`
                                : 'Closed'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">No hours configured</span>
                    )}
                  </div>
                </div>

                {this.renderPaymentCard()}
                {this.props.access === 'ADMIN' && this.renderQuickBooksCard()}
                {this.renderCompanySettingsCard(false)}
              </>
            ) : (
              <>
                {/* ── Company-Level View ───────────────────────────── */}

                {this.renderCompanyInfoCard()}
                {this.renderCompanySettingsCard(true)}
              </>
            )}
          </div>

          {/* Right Column - Status & Quick Info (company-level only) */}
          {!isLocationLevel && (
            <div className="col-md-4">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Account Status</h5>
                </div>
                <div className="card-body">
                  {installer && (
                    <>
                      <div className="mb-3">
                        <strong>Account Type:</strong><br />
                        <span className={`badge ${installer.is_trial ? 'badge-warning' : 'badge-success'}`}>
                          {installer.is_trial ? 'Trial Specialist' : 'Active Specialist'}
                        </span>
                      </div>
                      <div className="mb-3">
                        <strong>Subscription:</strong><br />
                        {installer.subscription || 'ASSIST'}
                      </div>
                      <div className="mb-3">
                        <strong>Payment Status:</strong><br />
                        <span className={`badge ${installer.onboarding_complete ? 'badge-success' : 'badge-warning'}`}>
                          {installer.onboarding_complete ? 'Setup Complete' : 'Setup Required'}
                        </span>
                        {installer.onboarding_complete && this.state.stripeCapabilities &&
                          Object.keys(this.state.stripeCapabilities).length > 0 &&
                          this.state.stripeCapabilities.us_bank_account_ach_payments !== 'active' && (
                          <div className="mt-1">
                            <span className="badge badge-warning">
                              <i className="fa-sharp fa-regular fa-exclamation-triangle mr-1"></i> Capabilities Pending
                            </span>
                          </div>
                        )}
                      </div>
                      {this.props.access === 'ADMIN' && (
                        <div className="mb-3">
                          <strong>QuickBooks:</strong><br />
                          {this.state.qbo && this.state.qbo.connected && !this.state.qbo.token_expired ? (
                            this.state.qbo.status === 'active' ? (
                              <span className="badge badge-success">
                                <i className="fa-sharp fa-regular fa-check mr-1"></i> Connected
                              </span>
                            ) : (
                              <span className="badge badge-warning">
                                <i className="fa-sharp fa-regular fa-gear mr-1"></i> Setup Required
                              </span>
                            )
                          ) : this.state.qbo && this.state.qbo.token_expired ? (
                            <span className="badge badge-warning">
                              <i className="fa-sharp fa-regular fa-exclamation-triangle mr-1"></i> Expired
                            </span>
                          ) : (
                            <span className="badge badge-secondary">
                              <i className="fa-sharp fa-regular fa-link mr-1"></i> Not Connected
                            </span>
                          )}
                        </div>
                      )}
                      {!!installer.is_trial && (
                        <div className="mb-3">
                          <strong>Trial Jobs:</strong><br />
                          {installer.trial_jobs_completed || 0} completed
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Need Help?</h5>
                </div>
                <div className="card-body">
                  <p>Having trouble with setup or have questions?</p>
                  <a href="mailto:support@jobvious.com" className="btn btn-outline-primary btn-sm">
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Locations Section (company-level, multi-loc only) ────── */}
        {!isLocationLevel && !isSingleLocCombined && isInstaller && locations.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              {multiLoc ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0">
                      <i className="fa-sharp fa-regular fa-map-marker-alt mr-2"></i>
                      Locations
                      <span className="badge badge-secondary ml-2">{locations.length}</span>
                    </h3>
                    {isOwnerOrAdmin && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={this.handleOpenAddLocationModal}
                      >
                        <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Location
                      </button>
                    )}
                  </div>
                  {locations.map(loc =>
                    this.renderLocationSummary(loc, locationData[loc.location_id])
                  )}
                </>
              ) : (
                <>
                  <h3 className="mb-3">
                    <i className="fa-sharp fa-regular fa-map-marker-alt mr-2"></i>
                    Location
                  </h3>
                  <div className="card mb-4">
                    <div className="card-body">
                      {this.renderLocationDetail(locations[0], locationData[locations[0].location_id], { readOnly: true })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Modals ──────────────────────────────────────────────── */}

        {/* Company Settings Modal */}
        <Modal show={showSettingsModal} onHide={this.handleCloseSettingsModal}>
          <Modal.Header closeButton>
            <Modal.Title>Company Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label className="font-weight-bold">Materials Tracking</label>
              <p className="text-muted small mb-2">
                When enabled, technicians will be prompted to record materials used when finishing applicable services.
              </p>
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="materialsTrackingSwitch"
                  checked={settingsForm.materials_tracking_enabled}
                  onChange={(e) => this.handleSettingsChange('materials_tracking_enabled', e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="materialsTrackingSwitch">
                  {settingsForm.materials_tracking_enabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>
            <hr />
            <div className="form-group mb-0">
              <label className="font-weight-bold">Time Tracking</label>
              <p className="text-muted small mb-2">
                Track staff check-in/out times for attendance and payroll purposes.
              </p>
              {TIME_TRACKING_OPTIONS.map(option => (
                <div className="custom-control custom-radio mb-2" key={option.value}>
                  <input
                    type="radio"
                    className="custom-control-input"
                    id={`timeTracking_${option.value}`}
                    name="timeTrackingMode"
                    value={option.value}
                    checked={settingsForm.time_tracking_mode === option.value}
                    onChange={(e) => this.handleSettingsChange('time_tracking_mode', e.target.value)}
                  />
                  <label className="custom-control-label" htmlFor={`timeTracking_${option.value}`}>
                    <strong>{option.label}</strong>
                    <span className="text-muted ml-2">— {option.description}</span>
                  </label>
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseSettingsModal} disabled={settingsLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSaveSettings} disabled={settingsLoading}>
              {settingsLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Calendar Selection Modal */}
        <Modal show={showCalendarSelection} onHide={this.handleCloseCalendarSelection}>
          <Modal.Header closeButton>
            <Modal.Title>Select Calendar</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">
              Choose which Google Calendar to sync jobs for this location:
            </p>
            <div className="list-group">
              {availableCalendars.map(calendar => (
                <button
                  key={calendar.id}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  onClick={() => this.handleCalendarSelection(calendar)}
                >
                  <div>
                    <strong>{calendar.name}</strong>
                    {calendar.primary && (
                      <span className="badge badge-info ml-2">Primary</span>
                    )}
                  </div>
                  <i className="fa-sharp fa-regular fa-chevron-right text-muted"></i>
                </button>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseCalendarSelection}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Operating Hours Modal */}
        <Modal show={showOperatingHoursModal} onHide={this.handleCloseOperatingHoursModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Operating Hours</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-4">
              Set your business hours for each day of the week. Closed days will not be available for scheduling.
            </p>
            <div className="operating-hours-form">
              {operatingHoursForm.map((day, index) => (
                <div key={index} className="form-row align-items-center py-3 border-bottom">
                  <div className="col-md-3">
                    <strong>{day.day_name || DAY_NAMES[day.day_of_week]}</strong>
                  </div>
                  <div className="col-md-2">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`day-open-${index}`}
                        checked={day.is_open}
                        onChange={(e) => this.handleOperatingHoursChange(index, 'is_open', e.target.checked)}
                      />
                      <label className="custom-control-label" htmlFor={`day-open-${index}`}>
                        {day.is_open ? 'Open' : 'Closed'}
                      </label>
                    </div>
                  </div>
                  {day.is_open && (
                    <>
                      <div className="col-md-3">
                        <label className="sr-only">Open Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={day.open_time || '08:00'}
                          onChange={(e) => this.handleOperatingHoursChange(index, 'open_time', e.target.value)}
                        />
                      </div>
                      <div className="col-md-1 text-center">to</div>
                      <div className="col-md-3">
                        <label className="sr-only">Close Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={day.close_time || '17:00'}
                          onChange={(e) => this.handleOperatingHoursChange(index, 'close_time', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseOperatingHoursModal} disabled={operatingHoursLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSaveOperatingHours} disabled={operatingHoursLoading}>
              {operatingHoursLoading ? 'Saving...' : 'Save Hours'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Holidays Modal */}
        <Modal show={this.state.showHolidaysModal} onHide={this.handleCloseHolidaysModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Holiday Closures</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-4">
              Select holidays when your shop will be closed. Changes are saved immediately.
            </p>

            {this.state.holidaysLoading && (
              <div className="text-center mb-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            {/* Current Year Holidays — only future */}
            {(() => {
              const today = moment().startOf('day');
              const currentYear = moment().year();
              const currentYearHolidays = US_HOLIDAYS.filter(holiday =>
                moment(holiday.getDate(currentYear)).isSameOrAfter(today)
              );
              if (currentYearHolidays.length === 0) return null;
              return (
                <>
                  <h6 className="mb-3">{currentYear} Holidays</h6>
                  <div className="holiday-checklist mb-4">
                    {currentYearHolidays.map(holiday => {
                      const date = holiday.getDate(currentYear);
                      const isBlocked = this.isDateBlocked(date);
                      return (
                        <div className="d-flex align-items-center py-2 border-bottom" key={`${holiday.id}-${currentYear}`}>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id={`holiday-${holiday.id}-${currentYear}`}
                              checked={isBlocked}
                              onChange={() => this.handleToggleHoliday(date, holiday.name)}
                              disabled={this.state.holidaysLoading}
                            />
                            <label className="custom-control-label" htmlFor={`holiday-${holiday.id}-${currentYear}`}>
                              <strong>{holiday.name}</strong>
                              <span className="text-muted ml-2">{formatHolidayDate(date)}</span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            {/* Next Year Holidays — only future */}
            {(() => {
              const today = moment().startOf('day');
              const nextYear = moment().year() + 1;
              const nextYearHolidays = US_HOLIDAYS.filter(holiday =>
                moment(holiday.getDate(nextYear)).isSameOrAfter(today)
              );
              if (nextYearHolidays.length === 0) return null;
              return (
                <>
                  <h6 className="mb-3">{nextYear} Holidays</h6>
                  <div className="holiday-checklist">
                    {nextYearHolidays.map(holiday => {
                      const date = holiday.getDate(nextYear);
                      const isBlocked = this.isDateBlocked(date);
                      return (
                        <div className="d-flex align-items-center py-2 border-bottom" key={`${holiday.id}-${nextYear}`}>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id={`holiday-${holiday.id}-${nextYear}`}
                              checked={isBlocked}
                              onChange={() => this.handleToggleHoliday(date, holiday.name)}
                              disabled={this.state.holidaysLoading}
                            />
                            <label className="custom-control-label" htmlFor={`holiday-${holiday.id}-${nextYear}`}>
                              <strong>{holiday.name}</strong>
                              <span className="text-muted ml-2">{formatHolidayDate(date)}</span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseHolidaysModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Custom Closures Modal */}
        <Modal show={this.state.showCustomClosuresModal} onHide={this.handleCloseCustomClosuresModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Custom Closures</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-4">
              Block date ranges for vacations, training, or other closures.
            </p>

            {/* Add New Closure Form */}
            <div className="card p-3 mb-4 bg-light">
              <h6 className="mb-3">Add New Closure</h6>
              <div className="form-row align-items-end">
                <div className="col-md-3">
                  <label className="small font-weight-bold">From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={this.state.newClosureStart}
                    onChange={(e) => this.setState({ newClosureStart: e.target.value })}
                    min={moment().format('YYYY-MM-DD')}
                  />
                </div>
                <div className="col-md-3">
                  <label className="small font-weight-bold">To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={this.state.newClosureEnd}
                    onChange={(e) => this.setState({ newClosureEnd: e.target.value })}
                    min={this.state.newClosureStart || moment().format('YYYY-MM-DD')}
                  />
                </div>
                <div className="col-md-4">
                  <label className="small font-weight-bold">Reason</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Vacation, Training"
                    value={this.state.newClosureReason}
                    onChange={(e) => this.setState({ newClosureReason: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={this.handleAddCustomClosure}
                    disabled={!this.state.newClosureStart || !this.state.newClosureEnd || !this.state.newClosureReason || this.state.closuresLoading}
                  >
                    {this.state.closuresLoading ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>

            {/* Existing Custom Closures List */}
            <h6 className="mb-3">Scheduled Closures</h6>
            {this.state.customClosures.length === 0 ? (
              <p className="text-muted">No custom closures scheduled.</p>
            ) : (
              <div className="closures-list">
                {this.state.customClosures.map((closure, idx) => (
                  <div className="d-flex justify-content-between align-items-center p-3 border-bottom" key={idx}>
                    <div>
                      <strong>{this.formatDateRange(closure.start_date, closure.end_date)}</strong>
                      <span className="text-muted ml-2">— {closure.reason}</span>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => this.handleRemoveClosure(closure)}
                      disabled={this.state.closuresLoading}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseCustomClosuresModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add Location Modal */}
        <Modal show={this.state.showAddLocationModal} onHide={this.handleCloseAddLocationModal}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.addLocationError && (
              <div className="alert alert-danger">{this.state.addLocationError}</div>
            )}
            <div className="form-group">
              <label className="font-weight-bold">Location Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Downtown Shop, North Location"
                value={this.state.addLocationForm.location_name}
                onChange={(e) => this.handleAddLocationChange('location_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="font-weight-bold">Address <span className="text-danger">*</span></label>
              <Autocomplete
                apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                className="form-control"
                onPlaceSelected={(place) => this.handleAddLocationPlaceSelected(place)}
                options={{
                  types: ['address'],
                  componentRestrictions: { country: 'us' }
                }}
                placeholder="Start typing an address..."
              />
            </div>

            {this.state.addLocationForm.address_line1 && (
              <div className="card bg-light p-3 mb-3">
                <small className="text-muted d-block mb-1">Parsed Address</small>
                <div>{this.state.addLocationForm.address_line1}</div>
                {this.state.addLocationForm.address_line2 && (
                  <div>{this.state.addLocationForm.address_line2}</div>
                )}
                <div>
                  {[this.state.addLocationForm.address_city, this.state.addLocationForm.address_state].filter(Boolean).join(', ')}{' '}
                  {this.state.addLocationForm.address_zip}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="font-weight-bold">Suite / Unit (optional)</label>
              <input
                type="text"
                className="form-control"
                placeholder="Suite, Unit, Building, etc."
                value={this.state.addLocationForm.address_line2}
                onChange={(e) => this.handleAddLocationChange('address_line2', e.target.value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseAddLocationModal} disabled={this.state.addLocationLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleAddLocationSubmit} disabled={this.state.addLocationLoading}>
              {this.state.addLocationLoading ? 'Adding...' : 'Add Location'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* QBO Setup Wizard Modal */}
        <QboSetupWizard
          show={this.state.showQboSetupWizard}
          onHide={this.handleCloseQboSetupWizard}
          onActivated={this.handleQboActivated}
          onAuthExpired={() => { this.handleCloseQboSetupWizard(); this.loadUserProfile(); }}
          installerId={this.state.installerId}
          locations={this.props.locations || []}
        />
      </div>
    );
  }
}

export default ProfilePage;
